import React, { useState } from 'react';
import { Form, Input, Dropdown, Button } from 'semantic-ui-react';

export type FilterType = 'phone' | 'email';

type FilterOption<T extends FilterType> = {
  key: string;
  text: string;
  value: T;
  icon?: string;
};

/**
 * `T` is the subset of `FilterType` a call site actually offers, inferred from
 * `filterOptions`, so `inputLabelMap` need only be total over the keys that site offers.
 * Fixing it to `FilterType` is what forced single-filter call sites to assert a one-key
 * literal into a two-key `Record`.
 */
type SearchFilterProps<T extends FilterType> = {
  /**
   * Available filters, most-preferred first. Non-empty by contract: the first entry is the
   * fallback for `filterType` when `defaultFilterType` is omitted, and the input label is
   * looked up by the active filter, so there is no coherent rendering with zero options.
   */
  filterOptions: [FilterOption<T>, ...FilterOption<T>[]];

  defaultFilterType?: T;
  onFilterChange: (payload: { type: T; value: string }) => void;

  filterTypeLabel?: string;
  inputLabelMap: Record<T, string>;
  inputHintMap?: Partial<Record<T, string>>;

  autoApply?: boolean;
  disabled?: boolean;
};

const SearchFilter = <T extends FilterType,>({
  filterOptions,
  defaultFilterType,
  onFilterChange,

  filterTypeLabel = 'Search By',
  inputLabelMap,
  inputHintMap,

  autoApply = false,
  disabled = false,
}: SearchFilterProps<T>) => {
  const hasMultipleFilters = filterOptions.length > 1;

  const initialType = defaultFilterType ?? filterOptions[0]?.value;

  const [filterType, setFilterType] = useState<T>(initialType);
  const [value, setValue] = useState('');

  const applyFilter = () => {
    onFilterChange({ type: filterType, value });
  };

  const handleValueChange = (val: string) => {
    setValue(val);
    if (autoApply) {
      onFilterChange({ type: filterType, value: val });
    }
  };

  return (
    <Form>
      <Form.Group widths="equal">
        {hasMultipleFilters && (
          <Form.Field width={4}>
            <label>{filterTypeLabel}</label>
            <Dropdown
              selection
              fluid
              options={filterOptions}
              value={filterType}
              disabled={disabled}
              onChange={(_, data) => {
                const newType = data.value as T;
                setFilterType(newType);
                setValue('');
                onFilterChange({ type: newType, value: '' });
              }}
            />
          </Form.Field>
        )}

        <Form.Field width={hasMultipleFilters ? 12 : 16}>
          <label>
            {inputLabelMap[filterType]}
            {inputHintMap?.[filterType] && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 12,
                  color: '#666',
                }}
              >
                {inputHintMap[filterType]}
              </span>
            )}
          </label>
          <Input
            fluid
            value={value}
            disabled={disabled}
            placeholder={inputLabelMap[filterType]}
            onChange={(e) => handleValueChange(e.target.value)}
            action={!autoApply && <Button primary icon="filter" content="Apply" onClick={applyFilter} />}
          />
        </Form.Field>
      </Form.Group>
    </Form>
  );
};

export default SearchFilter;
